import {
    AppwriteException,
    Client,
    type Payload,
    applyDefaults,
} from '../client';
import type { Models } from '../models';

import { UsageEventMetric } from '../enums/usage-event-metric';
import { UsageInterval } from '../enums/usage-interval';
import { UsageEventDimension } from '../enums/usage-event-dimension';
import { UsageOrderBy } from '../enums/usage-order-by';
import { UsageOrderDirection } from '../enums/usage-order-direction';
import { UsageGaugeMetric } from '../enums/usage-gauge-metric';
import { UsageGaugeDimension } from '../enums/usage-gauge-dimension';
export class Usage {
    client: Client;

    constructor(client: Client) {
        this.client = client;
    }

    /**
     * Aggregate usage event metrics. `metrics[]` (1-10) is required; the response always contains one entry per requested metric, each with its own `points[]` time series.
     *
     * **Two response shapes**:
     * - Omit `interval` for a flat top-N table — one point per dimension combination, no time axis. Useful for "top 10 paths by bandwidth in the last 7 days".
     * - Pass `interval` (`1m`, `15m`, `30m`, `1h`, `1d`) for a time series — one point per (time bucket × dimension combination).
     *
     * `dimensions[]` breaks each point down by one or more attributes (service, path, status, country, …). `queries[]` filters the underlying events using the standard Utopia query syntax — `equal("path", ["/v1/storage/files"])`, `equal("resourceType", ["bucket"])`, `equal("resourceId", ["abc123"])`, `startsWith("path", ["/v1/storage"])`, `equal("status", ["200", "201"])`, `isNotNull("resourceId")`. Supported attributes: see `queries[]` param. Supported methods: `equal`, `notEqual`, `contains`, `startsWith`, `endsWith`, `isNull`, `isNotNull`. Pass multiple metrics to render stacked charts in one round-trip. `orderBy=value`+`orderDir=desc`+`limit=N` returns the top-N by aggregated value. When `startAt` is omitted, the default window adapts to `interval` (or 7d when interval is omitted).
     *
     * @param {(UsageEventMetric | (string & {}))[]} params.metrics - One to ten metric names. Single-metric callers pass a one-element array. Example: `metrics[]=executions` or `metrics[]=executions&metrics[]=executions.compute` for stacked charts. On console: `metrics[]=affiliates.clicks`.
     * @param {string[]} params.queries - Up to 10 filter queries in Utopia syntax. Allowed attributes, also published as the `UsageEventDimension` enum: path, method, status, service, resourceType, resourceId, teamId, country, continentCode, city, region, hostname, ip, osName, clientType, clientName, deviceName, sdk, sdkVersion, protocol, queryKeys, accept, acceptLanguage, postalCode, latitude, longitude, timeZone, weatherCode, isp, autonomousSystemNumber, autonomousSystemOrganization, connectionType, connectionUsageType, connectionOrganization. Allowed methods: equal, notEqual, contains, startsWith, endsWith, isNull, isNotNull. Example: `queries[]=equal("resourceType", ["bucket"])`.
     * @param {UsageInterval} params.interval - Time interval size. Omit (null) for a flat aggregate over the whole window. Allowed: 1m, 15m, 30m, 1h, 1d.
     * @param {UsageEventDimension[]} params.dimensions - Break-down dimensions (max 10). Allowed: path, method, status, service, resourceType, country, continentCode, city, region, hostname, ip, osName, clientType, clientName, deviceName, sdk, sdkVersion, teamId, resourceId, protocol, queryKeys, accept, acceptLanguage, postalCode, latitude, longitude, timeZone, weatherCode, isp, autonomousSystemNumber, autonomousSystemOrganization, connectionType, connectionUsageType, connectionOrganization.
     * @param {string} params.startAt - Range start in ISO 8601. Defaults adapt to interval (7d for the no-interval aggregate).
     * @param {string} params.endAt - Range end in ISO 8601. Defaults to the current time.
     * @param {UsageOrderBy} params.orderBy - Column to order by. Allowed: time, value. Default time when an interval is set; otherwise value.
     * @param {UsageOrderDirection} params.orderDir - Sort direction: asc or desc. Default desc — paired with the default limit, returns the most recent / highest-value groups first.
     * @param {number} params.limit - Maximum rows to return (1-5000).
     * @param {number} params.offset - Pagination offset (0-100000).
     * @throws {AppwriteException}
     * @returns {Promise<Models.UsageEventList>}
     */
    listEvents(params: {
        metrics: (UsageEventMetric | (string & {}))[];
        queries?: string[];
        interval?: UsageInterval;
        dimensions?: UsageEventDimension[];
        startAt?: string;
        endAt?: string;
        orderBy?: UsageOrderBy;
        orderDir?: UsageOrderDirection;
        limit?: number;
        offset?: number;
    }): Promise<Models.UsageEventList>;
    /**
     * Aggregate usage event metrics. `metrics[]` (1-10) is required; the response always contains one entry per requested metric, each with its own `points[]` time series.
     *
     * **Two response shapes**:
     * - Omit `interval` for a flat top-N table — one point per dimension combination, no time axis. Useful for "top 10 paths by bandwidth in the last 7 days".
     * - Pass `interval` (`1m`, `15m`, `30m`, `1h`, `1d`) for a time series — one point per (time bucket × dimension combination).
     *
     * `dimensions[]` breaks each point down by one or more attributes (service, path, status, country, …). `queries[]` filters the underlying events using the standard Utopia query syntax — `equal("path", ["/v1/storage/files"])`, `equal("resourceType", ["bucket"])`, `equal("resourceId", ["abc123"])`, `startsWith("path", ["/v1/storage"])`, `equal("status", ["200", "201"])`, `isNotNull("resourceId")`. Supported attributes: see `queries[]` param. Supported methods: `equal`, `notEqual`, `contains`, `startsWith`, `endsWith`, `isNull`, `isNotNull`. Pass multiple metrics to render stacked charts in one round-trip. `orderBy=value`+`orderDir=desc`+`limit=N` returns the top-N by aggregated value. When `startAt` is omitted, the default window adapts to `interval` (or 7d when interval is omitted).
     *
     * @param {(UsageEventMetric | (string & {}))[]} metrics - One to ten metric names. Single-metric callers pass a one-element array. Example: `metrics[]=executions` or `metrics[]=executions&metrics[]=executions.compute` for stacked charts. On console: `metrics[]=affiliates.clicks`.
     * @param {string[]} queries - Up to 10 filter queries in Utopia syntax. Allowed attributes, also published as the `UsageEventDimension` enum: path, method, status, service, resourceType, resourceId, teamId, country, continentCode, city, region, hostname, ip, osName, clientType, clientName, deviceName, sdk, sdkVersion, protocol, queryKeys, accept, acceptLanguage, postalCode, latitude, longitude, timeZone, weatherCode, isp, autonomousSystemNumber, autonomousSystemOrganization, connectionType, connectionUsageType, connectionOrganization. Allowed methods: equal, notEqual, contains, startsWith, endsWith, isNull, isNotNull. Example: `queries[]=equal("resourceType", ["bucket"])`.
     * @param {UsageInterval} interval - Time interval size. Omit (null) for a flat aggregate over the whole window. Allowed: 1m, 15m, 30m, 1h, 1d.
     * @param {UsageEventDimension[]} dimensions - Break-down dimensions (max 10). Allowed: path, method, status, service, resourceType, country, continentCode, city, region, hostname, ip, osName, clientType, clientName, deviceName, sdk, sdkVersion, teamId, resourceId, protocol, queryKeys, accept, acceptLanguage, postalCode, latitude, longitude, timeZone, weatherCode, isp, autonomousSystemNumber, autonomousSystemOrganization, connectionType, connectionUsageType, connectionOrganization.
     * @param {string} startAt - Range start in ISO 8601. Defaults adapt to interval (7d for the no-interval aggregate).
     * @param {string} endAt - Range end in ISO 8601. Defaults to the current time.
     * @param {UsageOrderBy} orderBy - Column to order by. Allowed: time, value. Default time when an interval is set; otherwise value.
     * @param {UsageOrderDirection} orderDir - Sort direction: asc or desc. Default desc — paired with the default limit, returns the most recent / highest-value groups first.
     * @param {number} limit - Maximum rows to return (1-5000).
     * @param {number} offset - Pagination offset (0-100000).
     * @throws {AppwriteException}
     * @returns {Promise<Models.UsageEventList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    listEvents(
        metrics: (UsageEventMetric | (string & {}))[],
        queries?: string[],
        interval?: UsageInterval,
        dimensions?: UsageEventDimension[],
        startAt?: string,
        endAt?: string,
        orderBy?: UsageOrderBy,
        orderDir?: UsageOrderDirection,
        limit?: number,
        offset?: number,
    ): Promise<Models.UsageEventList>;
    listEvents(
        paramsOrFirst:
            | {
                  metrics: (UsageEventMetric | (string & {}))[];
                  queries?: string[];
                  interval?: UsageInterval;
                  dimensions?: UsageEventDimension[];
                  startAt?: string;
                  endAt?: string;
                  orderBy?: UsageOrderBy;
                  orderDir?: UsageOrderDirection;
                  limit?: number;
                  offset?: number;
              }
            | (UsageEventMetric | (string & {}))[],
        ...rest: [
            string[]?,
            UsageInterval?,
            UsageEventDimension[]?,
            string?,
            string?,
            UsageOrderBy?,
            UsageOrderDirection?,
            number?,
            number?,
        ]
    ): Promise<Models.UsageEventList> {
        let params: {
            metrics: (UsageEventMetric | (string & {}))[];
            queries?: string[];
            interval?: UsageInterval;
            dimensions?: UsageEventDimension[];
            startAt?: string;
            endAt?: string;
            orderBy?: UsageOrderBy;
            orderDir?: UsageOrderDirection;
            limit?: number;
            offset?: number;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst) &&
            ('metrics' in paramsOrFirst ||
                'queries' in paramsOrFirst ||
                'interval' in paramsOrFirst ||
                'dimensions' in paramsOrFirst ||
                'startAt' in paramsOrFirst ||
                'endAt' in paramsOrFirst ||
                'orderBy' in paramsOrFirst ||
                'orderDir' in paramsOrFirst ||
                'limit' in paramsOrFirst ||
                'offset' in paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                metrics: (UsageEventMetric | (string & {}))[];
                queries?: string[];
                interval?: UsageInterval;
                dimensions?: UsageEventDimension[];
                startAt?: string;
                endAt?: string;
                orderBy?: UsageOrderBy;
                orderDir?: UsageOrderDirection;
                limit?: number;
                offset?: number;
            };
        } else {
            params = {
                metrics: paramsOrFirst as (UsageEventMetric | (string & {}))[],
                queries: rest[0] as string[],
                interval: rest[1] as UsageInterval,
                dimensions: rest[2] as UsageEventDimension[],
                startAt: rest[3] as string,
                endAt: rest[4] as string,
                orderBy: rest[5] as UsageOrderBy,
                orderDir: rest[6] as UsageOrderDirection,
                limit: rest[7] as number,
                offset: rest[8] as number,
            };
        }

        const metrics = params.metrics;
        const queries = params.queries;
        const interval = params.interval;
        const dimensions = params.dimensions;
        const startAt = params.startAt;
        const endAt = params.endAt;
        const orderBy = params.orderBy;
        const orderDir = params.orderDir;
        const limit = params.limit;
        const offset = params.offset;

        if (typeof metrics === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "metrics"',
            );
        }
        const apiPath = '/usage/events';
        const apiPayload: Payload = {};
        if (typeof metrics !== 'undefined') {
            apiPayload['metrics'] = metrics;
        }
        if (typeof queries !== 'undefined') {
            apiPayload['queries'] = queries;
        }
        if (typeof interval !== 'undefined') {
            apiPayload['interval'] = interval;
        }
        if (typeof dimensions !== 'undefined') {
            apiPayload['dimensions'] = dimensions;
        }
        if (typeof startAt !== 'undefined') {
            apiPayload['startAt'] = startAt;
        }
        if (typeof endAt !== 'undefined') {
            apiPayload['endAt'] = endAt;
        }
        if (typeof orderBy !== 'undefined') {
            apiPayload['orderBy'] = orderBy;
        }
        if (typeof orderDir !== 'undefined') {
            apiPayload['orderDir'] = orderDir;
        }
        if (typeof limit !== 'undefined') {
            apiPayload['limit'] = limit;
        }
        if (typeof offset !== 'undefined') {
            apiPayload['offset'] = offset;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'usageEventList',
            models: {
                usageEventList: {
                    defaults: {
                        interval: '',
                        metrics: [],
                    },
                    properties: {
                        metrics: {
                            items: 'usageMetric',
                        },
                    },
                },
                usageMetric: {
                    defaults: {
                        metric: '',
                        points: [],
                    },
                    properties: {
                        points: {
                            items: 'usageDataPoint',
                        },
                    },
                },
                usageDataPoint: {
                    defaults: {
                        value: 0,
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Aggregate usage gauge snapshots. Gauges are point-in-time values (storage totals, resource counts, …); each point carries the latest snapshot in its interval via `argMax(value, time)`. `metrics[]` (1-10) is required; the response always contains one entry per requested metric, each with its own `points[]` time series.
     *
     * A metric with no stored samples in the window returns an empty `points[]`. A metric that really did read zero returns a point whose `value` is `0`, so "no such series" and "a genuine zero" are different answers.
     *
     * **Two response shapes**:
     * - Omit `interval` for a flat top-N table — `argMax(value, time)` per dimension combination over the whole window, no time axis. Useful for "top 10 resources by current storage".
     * - Pass `interval` (`1m`, `15m`, `30m`, `1h`, `1d`) for a time series — one snapshot per (time bucket × dimension combination).
     *
     * `dimensions[]` breaks each point down further. Supported on gauges: `resourceId`, `teamId`, `service`, `resourceType`, `ordinal`. `service` and `resourceType` enable per-service / per-resource-type panels (e.g. storage-by-service: group `files.storage`, `deployments.storage`, `builds.storage`, `databases.storage` by `service`). `ordinal` separates per-node series for multi-node resources such as dedicated databases. It is a stable per-node identity, not a role — ordinal 0 is the first member created, and a failover can leave the primary on any ordinal, so read the role from the database's replicas endpoint rather than inferring it here. `queries[]` filters the underlying rows using the standard Utopia query syntax — `equal("resourceType", ["bucket"])`, `equal("resourceId", ["abc123"])`, `equal("teamId", ["team_x"])`, `equal("ordinal", ["0"])`, `isNotNull("teamId")`. Supported attributes: see `queries[]` param. Supported methods: `equal`, `notEqual`, `isNull`, `isNotNull`. Pass multiple metrics to render stacked charts in one round-trip. `orderBy=value`+`orderDir=desc`+`limit=N` returns the top-N. When `startAt` is omitted, the default window adapts to interval (or 7d when interval is omitted).
     *
     * `aggregate` selects how the samples in a bucket are combined: `last` (default) is the latest reading — correct for a snapshot such as storage — while `max` is the highest reading. Use `max` for a sampled level series: peak concurrent realtime connections is `metrics[]=realtime.connections&aggregate=max`, at whatever `interval` the chart needs, since the peak of a set of samples is just the max of their per-bucket maxima. `realtime.connections` is served only here - it is a concurrency level, not a countable event, so `/v1/usage/events` rejects it.
     *
     * @param {(UsageGaugeMetric | (string & {}))[]} params.metrics - One to ten metric names. Single-metric callers pass a one-element array. Example: `metrics[]=files.storage` or `metrics[]=files.storage&metrics[]=deployments.storage` for stacked charts.
     * @param {string[]} params.queries - Up to 10 filter queries in Utopia syntax. Allowed attributes, also published as the `UsageGaugeDimension` enum: service, resourceType, resourceId, teamId, ordinal. Allowed methods: equal, notEqual, isNull, isNotNull. Example: `queries[]=equal("resourceType", ["bucket"])`.
     * @param {UsageInterval} params.interval - Time interval size. Omit (null) for a flat aggregate over the whole window. Allowed: 1m, 15m, 30m, 1h, 1d.
     * @param {UsageGaugeDimension[]} params.dimensions - Break-down dimensions. Allowed: resourceId, teamId, service, resourceType, ordinal.
     * @param {string} params.startAt - Range start in ISO 8601. Defaults to endAt - 7d.
     * @param {string} params.endAt - Range end in ISO 8601. Defaults to the current time.
     * @param {UsageOrderBy} params.orderBy - Column to order by. Allowed: time, value. Default time.
     * @param {UsageOrderDirection} params.orderDir - Sort direction: asc or desc. Default desc — paired with the default limit, this returns the most recent groups first. Pass asc for chronological charting.
     * @param {number} params.limit - Maximum rows to return (1-5000).
     * @param {number} params.offset - Pagination offset (0-100000).
     * @param {string} params.aggregate - How to combine the samples in each bucket. `last` (default) returns the latest reading — the right answer for a snapshot such as storage. `max` returns the highest reading, which is what a sampled level series needs: peak concurrent realtime connections is the max of `realtime.connections` over the window.
     * @throws {AppwriteException}
     * @returns {Promise<Models.UsageGaugeList>}
     */
    listGauges(params: {
        metrics: (UsageGaugeMetric | (string & {}))[];
        queries?: string[];
        interval?: UsageInterval;
        dimensions?: UsageGaugeDimension[];
        startAt?: string;
        endAt?: string;
        orderBy?: UsageOrderBy;
        orderDir?: UsageOrderDirection;
        limit?: number;
        offset?: number;
        aggregate?: string;
    }): Promise<Models.UsageGaugeList>;
    /**
     * Aggregate usage gauge snapshots. Gauges are point-in-time values (storage totals, resource counts, …); each point carries the latest snapshot in its interval via `argMax(value, time)`. `metrics[]` (1-10) is required; the response always contains one entry per requested metric, each with its own `points[]` time series.
     *
     * A metric with no stored samples in the window returns an empty `points[]`. A metric that really did read zero returns a point whose `value` is `0`, so "no such series" and "a genuine zero" are different answers.
     *
     * **Two response shapes**:
     * - Omit `interval` for a flat top-N table — `argMax(value, time)` per dimension combination over the whole window, no time axis. Useful for "top 10 resources by current storage".
     * - Pass `interval` (`1m`, `15m`, `30m`, `1h`, `1d`) for a time series — one snapshot per (time bucket × dimension combination).
     *
     * `dimensions[]` breaks each point down further. Supported on gauges: `resourceId`, `teamId`, `service`, `resourceType`, `ordinal`. `service` and `resourceType` enable per-service / per-resource-type panels (e.g. storage-by-service: group `files.storage`, `deployments.storage`, `builds.storage`, `databases.storage` by `service`). `ordinal` separates per-node series for multi-node resources such as dedicated databases. It is a stable per-node identity, not a role — ordinal 0 is the first member created, and a failover can leave the primary on any ordinal, so read the role from the database's replicas endpoint rather than inferring it here. `queries[]` filters the underlying rows using the standard Utopia query syntax — `equal("resourceType", ["bucket"])`, `equal("resourceId", ["abc123"])`, `equal("teamId", ["team_x"])`, `equal("ordinal", ["0"])`, `isNotNull("teamId")`. Supported attributes: see `queries[]` param. Supported methods: `equal`, `notEqual`, `isNull`, `isNotNull`. Pass multiple metrics to render stacked charts in one round-trip. `orderBy=value`+`orderDir=desc`+`limit=N` returns the top-N. When `startAt` is omitted, the default window adapts to interval (or 7d when interval is omitted).
     *
     * `aggregate` selects how the samples in a bucket are combined: `last` (default) is the latest reading — correct for a snapshot such as storage — while `max` is the highest reading. Use `max` for a sampled level series: peak concurrent realtime connections is `metrics[]=realtime.connections&aggregate=max`, at whatever `interval` the chart needs, since the peak of a set of samples is just the max of their per-bucket maxima. `realtime.connections` is served only here - it is a concurrency level, not a countable event, so `/v1/usage/events` rejects it.
     *
     * @param {(UsageGaugeMetric | (string & {}))[]} metrics - One to ten metric names. Single-metric callers pass a one-element array. Example: `metrics[]=files.storage` or `metrics[]=files.storage&metrics[]=deployments.storage` for stacked charts.
     * @param {string[]} queries - Up to 10 filter queries in Utopia syntax. Allowed attributes, also published as the `UsageGaugeDimension` enum: service, resourceType, resourceId, teamId, ordinal. Allowed methods: equal, notEqual, isNull, isNotNull. Example: `queries[]=equal("resourceType", ["bucket"])`.
     * @param {UsageInterval} interval - Time interval size. Omit (null) for a flat aggregate over the whole window. Allowed: 1m, 15m, 30m, 1h, 1d.
     * @param {UsageGaugeDimension[]} dimensions - Break-down dimensions. Allowed: resourceId, teamId, service, resourceType, ordinal.
     * @param {string} startAt - Range start in ISO 8601. Defaults to endAt - 7d.
     * @param {string} endAt - Range end in ISO 8601. Defaults to the current time.
     * @param {UsageOrderBy} orderBy - Column to order by. Allowed: time, value. Default time.
     * @param {UsageOrderDirection} orderDir - Sort direction: asc or desc. Default desc — paired with the default limit, this returns the most recent groups first. Pass asc for chronological charting.
     * @param {number} limit - Maximum rows to return (1-5000).
     * @param {number} offset - Pagination offset (0-100000).
     * @param {string} aggregate - How to combine the samples in each bucket. `last` (default) returns the latest reading — the right answer for a snapshot such as storage. `max` returns the highest reading, which is what a sampled level series needs: peak concurrent realtime connections is the max of `realtime.connections` over the window.
     * @throws {AppwriteException}
     * @returns {Promise<Models.UsageGaugeList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    listGauges(
        metrics: (UsageGaugeMetric | (string & {}))[],
        queries?: string[],
        interval?: UsageInterval,
        dimensions?: UsageGaugeDimension[],
        startAt?: string,
        endAt?: string,
        orderBy?: UsageOrderBy,
        orderDir?: UsageOrderDirection,
        limit?: number,
        offset?: number,
        aggregate?: string,
    ): Promise<Models.UsageGaugeList>;
    listGauges(
        paramsOrFirst:
            | {
                  metrics: (UsageGaugeMetric | (string & {}))[];
                  queries?: string[];
                  interval?: UsageInterval;
                  dimensions?: UsageGaugeDimension[];
                  startAt?: string;
                  endAt?: string;
                  orderBy?: UsageOrderBy;
                  orderDir?: UsageOrderDirection;
                  limit?: number;
                  offset?: number;
                  aggregate?: string;
              }
            | (UsageGaugeMetric | (string & {}))[],
        ...rest: [
            string[]?,
            UsageInterval?,
            UsageGaugeDimension[]?,
            string?,
            string?,
            UsageOrderBy?,
            UsageOrderDirection?,
            number?,
            number?,
            string?,
        ]
    ): Promise<Models.UsageGaugeList> {
        let params: {
            metrics: (UsageGaugeMetric | (string & {}))[];
            queries?: string[];
            interval?: UsageInterval;
            dimensions?: UsageGaugeDimension[];
            startAt?: string;
            endAt?: string;
            orderBy?: UsageOrderBy;
            orderDir?: UsageOrderDirection;
            limit?: number;
            offset?: number;
            aggregate?: string;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst) &&
            ('metrics' in paramsOrFirst ||
                'queries' in paramsOrFirst ||
                'interval' in paramsOrFirst ||
                'dimensions' in paramsOrFirst ||
                'startAt' in paramsOrFirst ||
                'endAt' in paramsOrFirst ||
                'orderBy' in paramsOrFirst ||
                'orderDir' in paramsOrFirst ||
                'limit' in paramsOrFirst ||
                'offset' in paramsOrFirst ||
                'aggregate' in paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                metrics: (UsageGaugeMetric | (string & {}))[];
                queries?: string[];
                interval?: UsageInterval;
                dimensions?: UsageGaugeDimension[];
                startAt?: string;
                endAt?: string;
                orderBy?: UsageOrderBy;
                orderDir?: UsageOrderDirection;
                limit?: number;
                offset?: number;
                aggregate?: string;
            };
        } else {
            params = {
                metrics: paramsOrFirst as (UsageGaugeMetric | (string & {}))[],
                queries: rest[0] as string[],
                interval: rest[1] as UsageInterval,
                dimensions: rest[2] as UsageGaugeDimension[],
                startAt: rest[3] as string,
                endAt: rest[4] as string,
                orderBy: rest[5] as UsageOrderBy,
                orderDir: rest[6] as UsageOrderDirection,
                limit: rest[7] as number,
                offset: rest[8] as number,
                aggregate: rest[9] as string,
            };
        }

        const metrics = params.metrics;
        const queries = params.queries;
        const interval = params.interval;
        const dimensions = params.dimensions;
        const startAt = params.startAt;
        const endAt = params.endAt;
        const orderBy = params.orderBy;
        const orderDir = params.orderDir;
        const limit = params.limit;
        const offset = params.offset;
        const aggregate = params.aggregate;

        if (typeof metrics === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "metrics"',
            );
        }
        const apiPath = '/usage/gauges';
        const apiPayload: Payload = {};
        if (typeof metrics !== 'undefined') {
            apiPayload['metrics'] = metrics;
        }
        if (typeof queries !== 'undefined') {
            apiPayload['queries'] = queries;
        }
        if (typeof interval !== 'undefined') {
            apiPayload['interval'] = interval;
        }
        if (typeof dimensions !== 'undefined') {
            apiPayload['dimensions'] = dimensions;
        }
        if (typeof startAt !== 'undefined') {
            apiPayload['startAt'] = startAt;
        }
        if (typeof endAt !== 'undefined') {
            apiPayload['endAt'] = endAt;
        }
        if (typeof orderBy !== 'undefined') {
            apiPayload['orderBy'] = orderBy;
        }
        if (typeof orderDir !== 'undefined') {
            apiPayload['orderDir'] = orderDir;
        }
        if (typeof limit !== 'undefined') {
            apiPayload['limit'] = limit;
        }
        if (typeof offset !== 'undefined') {
            apiPayload['offset'] = offset;
        }
        if (typeof aggregate !== 'undefined') {
            apiPayload['aggregate'] = aggregate;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'usageGaugeList',
            models: {
                usageGaugeList: {
                    defaults: {
                        interval: '',
                        metrics: [],
                    },
                    properties: {
                        metrics: {
                            items: 'usageMetric',
                        },
                    },
                },
                usageMetric: {
                    defaults: {
                        metric: '',
                        points: [],
                    },
                    properties: {
                        points: {
                            items: 'usageDataPoint',
                        },
                    },
                },
                usageDataPoint: {
                    defaults: {
                        value: 0,
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }
}
